import MuiMenuItem from '@material-ui/core/MenuItem';
import { Select } from '@monorepo/shared/componentsV2/Select/Select';
import { FieldIconType } from 'mapistry-shared';
import React, { useMemo } from 'react';
import { useField, UseFieldConfig } from 'react-final-form';
import styled from 'styled-components';
import { ColumnIconFactory } from '../../../logs/src/components/ColumnIconFactory';

type SelectQueryColumnFieldProps<ValueType> = Omit<
  React.ComponentProps<typeof Select>,
  'value' | 'onChange' | 'error' | 'errorText' | 'options'
> & {
  initialValue?: ValueType;
  validate?: UseFieldConfig<ValueType>['validate'];
  options: QueryColumnSelectOption[];
};

type SelectValue = React.ComponentProps<typeof MuiMenuItem>['value'];

type QueryColumnSelectOption = {
  fieldIconType: FieldIconType;
  label: string | React.ReactElement;
  value: Exclude<SelectValue, undefined>;
};

const StyledIcon = styled.div`
  & svg {
    display: inline-flex;
    height: 0.8rem;
    margin-top: 0.2rem;
    margin-right: 0.5rem;
    fill: ${({ theme }) => theme.colors.gray666} !important;
    stroke: ${({ theme }) => theme.colors.gray666} !important;
  }
`;

function getOptionIcon(
  option: QueryColumnSelectOption,
): React.ReactElement | string {
  if (option.fieldIconType) {
    return (
      <>
        <StyledIcon>
          {ColumnIconFactory({ fieldIconType: option.fieldIconType })}
        </StyledIcon>
        {option.label}
      </>
    );
  }
  return option.label;
}

export const SelectQueryColumnField = <ValueType extends string = string>({
  name,
  validate,
  initialValue,
  ...rest
}: SelectQueryColumnFieldProps<ValueType>) => {
  const { input, meta } = useField<ValueType>(name, { validate, initialValue });
  const hasError = !!meta.error && (meta.touched || meta.modified);
  const optionsWithIcons = useMemo(
    () =>
      rest.options.map((option) => ({
        ...option,
        label: getOptionIcon(option),
      })),
    [rest.options],
  );

  return (
    <Select
      {...rest}
      error={hasError}
      errorText={hasError ? meta.error : undefined}
      name={name}
      onChange={input.onChange}
      value={input.value}
      options={optionsWithIcons}
    />
  );
};
